import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../common/SharedStyles.css';
import PageContainer from '../common/PageContainer';
import WordItem from '../common/WordItem';
import { useAppContext } from '../../App';

interface VocabularyEntry {
  _id: string;
  word: string;
  definition: string;
  phonetics?: string;
  phonics?: string;
  examples: string[];
  tags: string[];
  searchCount: number;
  lastSearched: Date | string;
  source?: 'openai' | 'fallback';
}

const MyWordsPage = () => {
  const [vocabulary, setVocabulary] = useState<VocabularyEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date(0)); // Initialize with epoch time
  const refreshInProgressRef = useRef(false);
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  
  // Move loadVocabulary to a useCallback to avoid recreating it on each render
  const loadVocabulary = useCallback(async (preserveExpanded: boolean = true, preserveOrder: boolean = false) => {
    // Don't refresh if one is already in progress
    if (refreshInProgressRef.current) return;
    
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to view your words');
      setLoading(false);
      return;
    }
    
    // Only show loading indicator for initial load, not refreshes
    if (!preserveExpanded) {
      setLoading(true);
    }
    
    refreshInProgressRef.current = true;
    
    try {
      const response = await fetch('/api/search/vocabulary', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const vocabEntries = await response.json() as VocabularyEntry[];
        
        // Create a copy of the current vocabulary for reference
        const currentVocab = [...vocabulary];
        
        // If preserveOrder is true, maintain the current order of items
        // This prevents position shifts during refresh operations
        if (preserveOrder && currentVocab.length > 0) {
          console.log('Preserving list order during refresh');
          
          // Create a map of the new entries by ID for quick lookup
          const newEntriesMap = new Map(vocabEntries.map(entry => [entry._id, entry]));
          
          // Update existing entries while keeping their positions
          setVocabulary(prev => {
            return prev.map(item => {
              // If we have updated data for this item, use it
              if (newEntriesMap.has(item._id)) {
                const updatedItem = newEntriesMap.get(item._id);
                return {
                  ...item,
                  ...updatedItem
                };
              }
              return item;
            });
          });
        } else {
          // Normal load with sorting - used for initial loads or explicit sorts
          // Sort by last searched date (most recent first)
          const sortedEntries = vocabEntries.sort((a, b) => 
            new Date(b.lastSearched).getTime() - new Date(a.lastSearched).getTime() ||
            b.searchCount - a.searchCount
          );
          
          // Update vocabulary while preserving expanded state
          setVocabulary(prevVocab => {
            // For any existing entries, preserve any properties not in the new data
            // that might be important for UI state
            if (preserveExpanded && prevVocab.length > 0) {
              return sortedEntries.map(entry => {
                const existingEntry = prevVocab.find(e => e._id === entry._id);
                if (existingEntry) {
                  return { ...existingEntry, ...entry };
                }
                return entry;
              });
            }
            
            return sortedEntries;
          });
        }
      } else {
        setError('Failed to load your vocabulary');
      }
    } catch (error) {
      setError('An error occurred while loading your vocabulary');
      console.error('Error loading vocabulary:', error);
    } finally {
      setLoading(false);
      refreshInProgressRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove vocabulary from dependencies to break the infinite loop
  
  // Initial load
  useEffect(() => {
    // Initial load should reset expanded state
    loadVocabulary(false);
  }, [loadVocabulary]);
  
  // Separate effect for handling refreshes
  useEffect(() => {
    // Skip the first render or when value is epoch
    if (lastRefreshed.getTime() === new Date(0).getTime()) return;
    
    // Only reload on refresh, preserving expanded state and order to prevent folding
    loadVocabulary(true, true);
  }, [lastRefreshed, loadVocabulary]);
  
  // This is a safer refresh method that won't trigger immediately
  // but will allow the component to request a refresh when needed
  const handleRefresh = (wordId?: string) => {
    console.log(`Word ${wordId} refreshed - maintaining expanded state and position`);
    
    // If a specific word was refreshed, make sure it stays expanded
    if (wordId) {
      // First, ensure the expanded state is set for this word
      setExpandedItems(prev => ({
        ...prev,
        [wordId]: true
      }));
      
      // Instead of reloading all vocabulary, just update the specific item
      // The server has already updated the database entry, we just need
      // to request the single item and update it in our local state
      const updateSingleWord = async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token) return;
          
          // Prevent infinite update cycles
          if (refreshInProgressRef.current) return;
          refreshInProgressRef.current = true;
          
          // Fetch fresh data for just this word
          const response = await fetch(`/api/search/define?word=${encodeURIComponent(
            vocabulary.find(item => item._id === wordId)?.word || ""
          )}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const updatedData = await response.json();
            
            // Update just this one word in the vocabulary WITHOUT changing its position
            setVocabulary(prev => {
              return prev.map(item => {
                if (item._id === wordId) {
                  console.log(`Updating single word ${wordId} in place without changing position`);
                  return {
                    ...item,
                    definition: updatedData.definition,
                    definitions: updatedData.definitions,
                    phonetics: updatedData.phonetics,
                    phonics: updatedData.phonics,
                    examples: updatedData.examples,
                    source: updatedData.source,
                    searchCount: (item.searchCount || 0) + 1,
                    // Note: we intentionally DO NOT update lastSearched to avoid re-sorting
                    // This is key to preventing the position change that causes folding
                  };
                }
                return item;
              });
            });
          }
        } catch (error) {
          console.error('Error updating single word:', error);
        } finally {
          // Make sure to end the refresh state
          refreshInProgressRef.current = false;
        }
      };
      
      // Run the update
      updateSingleWord();
    } else {
      // If no specific word ID, do a full refresh as before
      // But ensure we preserve order to prevent folding
      // Prevent multiple refreshes in quick succession
      if (refreshInProgressRef.current) return;
      
      setTimeout(() => {
        setLastRefreshed(new Date());
      }, 500);
    }
  };
  
  const handleWordClick = (word: string) => {
    navigate(`/?word=${encodeURIComponent(word)}`);
  };
  
  const handleBackToSearch = () => {
    navigate('/');
  };
  
  const toggleItemExpand = (itemId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  const handleRemoveWord = async (wordId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to remove words');
      return;
    }
    
    try {
      const response = await fetch(`/api/search/vocabulary/${wordId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Update the local state by removing the deleted word
        setVocabulary(prev => prev.filter(entry => entry._id !== wordId));
        
        // Also update the search history in the global context
        // This ensures the word is removed from Recent Searches on the main page
        dispatch({
          type: 'SET_HISTORY',
          payload: state.searchHistory.filter(item => item._id !== wordId)
        });
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to remove word');
      }
    } catch (error) {
      console.error('Error removing word:', error);
      setError('An error occurred while removing the word');
    }
  };
  
  const formatSearchInfo = (entry: VocabularyEntry) => {
    const count = entry.searchCount > 1 ? `${entry.searchCount} times` : 'once';
    const date = new Date(entry.lastSearched).toLocaleDateString();
    return `Searched ${count} · Last: ${date}`;
  };
  
  return (
    <PageContainer>
      <div className="page-container">
        <div className="page-header">
          <h1>My Vocabulary</h1>
          <button className="primary-button" onClick={handleBackToSearch}>
            Back to Search
          </button>
        </div>
        
        {loading ? (
          <div className="loading-spinner">Loading...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : vocabulary.length === 0 ? (
          <div className="empty-state">
            <p>You haven't searched for any words yet.</p>
            <button className="primary-button" onClick={handleBackToSearch}>
              Search Now
            </button>
          </div>
        ) : (
          <div className="words-container">
            <div className="count-display">
              You have {vocabulary.length} unique words in your vocabulary
            </div>
            <ul className="words-list">
              {vocabulary.map((entry) => (
                <WordItem
                  key={`word-${entry._id}`}
                  item={entry}
                  isExpanded={expandedItems[entry._id]}
                  onToggleExpand={toggleItemExpand}
                  onRemove={handleRemoveWord}
                  onClick={handleWordClick}
                  formatSubtitle={formatSearchInfo}
                  className="vocab-item"
                  onRefresh={() => handleRefresh(entry._id)}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default MyWordsPage; 